import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './audiencias.css';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import Modal from '../../components/modal';
import SearchInternal from '../../components/SearchInternal/SearchInternal';
import VisibleAccess from '../../permissions/VisibleAccess';
import ReactPaginate from 'react-paginate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faGavel, faPen, faEye } from '@fortawesome/free-solid-svg-icons';
import { useHearings } from '../../context/hearingsContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import { useInternals } from '../../context/internalContext';
import { useUsers } from '../../context/usersContext';
import { useUnits } from '../../context/unitsContext';
import useSubmitLock from '../../utils/useSubmitLock';
import BinaryFileLink from '../../components/BinaryFileLink';
import { BINARY_RESOURCES, hasBinaryField } from '../../utils/binaryFile';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const todayStr = () => new Date().toISOString().split('T')[0];

const normalizeSearchText = (value) => {
  if (!value) return '';
  return value.toString().trim().replace(/\s+/g, ' ');
};

const STATUS_LABEL_MAP = {
  SCHEDULED: 'Agendada',
  DONE: 'Realizada',
  NOT_DONE: 'Não realizada',
  IN_PROGRESS: 'Em andamento',
};

const statusBadge = (hearing) => {
  if (hearing.in_progress) return { label: 'Em andamento', cls: 'hearing-badge--progress' };
  if (hearing.completion_status === true) return { label: 'Realizada', cls: 'hearing-badge--done' };
  if (hearing.completion_status === false) return { label: 'Não realizada', cls: 'hearing-badge--notdone' };
  const label = STATUS_LABEL_MAP[hearing.status_label] || 'Agendada';
  return { label, cls: 'hearing-badge--scheduled' };
};

const resolveHearingLink = (value) => {
  if (!value) return null;
  return { href: value, label: 'Abrir link' };
};

const formatHearingDate = (isoString) => {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const EMPTY_FORM = {
  internal: '',
  internalName: '',
  internalUnit: '',
  start_date: '',
  end_date: '',
  virtual: false,
  judicial_district: '',
  court: '',
  court_id: '',
  process: '',
  whatsapp_link: '',
  observations: '',
};

export default function Audiencias() {
  const { createHearing, updateHearing, listHearings, startHearingProgress, updateHearingStatus, uploadHearingProof } = useHearings();
  const { listCourts, getSchedulingById } = useAuxiliaries();
  const { getProcessInternalById } = useInternals();
  const { effectiveUnitId, userInfo } = useUsers();
  const { listUnits } = useUnits();
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const isPrivilegedUser = useMemo(() => {
    const groups = (userInfo?.groups || []).map((g) =>
      String(g).normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase()
    );
    return groups.includes('DUAP');
  }, [userInfo]);

  const [counts, setCounts] = useState({ total: 0, hoje: 0, agendadas: 0, realizadas: 0 });
  const [todayHearings, setTodayHearings] = useState([]);
  const [hearings, setHearings] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({
    internal_name: '',
    court_name: '',
    completion_status: '',
    start_date_from: '',
    start_date_to: '',
    unit: '',
  });

  const [units, setUnits] = useState([]);

  const [internalProcesses, setInternalProcesses] = useState([]);
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const [statusModal, setStatusModal] = useState({ open: false, hearing: null });
  const [statusForm, setStatusForm] = useState({ completion_status: '', observations: '' });
  const [uploadingId, setUploadingId] = useState(null);
  const [virtualMap, setVirtualMap] = useState({});
  const [viewModal, setViewModal] = useState({ open: false, hearing: null });

  // Estados do autocomplete comarca/vara
  const [courtSearchUi, setCourtSearchUi] = useState({
    showDistrictSuggestions: false,
    showCourtSuggestions: false,
  });
  const [courtLoading, setCourtLoading] = useState({ districts: false, courts: false });
  const [districtResults, setDistrictResults] = useState([]);
  const [courtResults, setCourtResults] = useState([]);

  // Sugestões únicas de comarca
  const filteredDistrictSuggestions = useMemo(() => {
    return [...new Set(
      districtResults
        .map((item) => item?.district)
        .filter(Boolean)
        .map(String)
    )].sort((a, b) => a.localeCompare(b, 'pt-BR')).slice(0, 40);
  }, [districtResults]);

  // Sugestões únicas de vara
  const filteredCourtSuggestions = useMemo(() => {
    return [...new Set(
      courtResults
        .map((item) => item?.unit)
        .filter(Boolean)
        .map(String)
    )].sort((a, b) => a.localeCompare(b, 'pt-BR')).slice(0, 40);
  }, [courtResults]);


  useEffect(() => {
    if (!isPrivilegedUser) return;
    listUnits().then((data) => {
      const list = Array.isArray(data) ? data : (data?.results || []);
      setUnits(list.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')));
    });
  }, [isPrivilegedUser, listUnits]);

  // Busca comarcas com debounce
  useEffect(() => {
    let isMounted = true;
    const search = normalizeSearchText(formData.judicial_district);

    if (!courtSearchUi.showDistrictSuggestions || search.length < 2) {
      setDistrictResults([]);
      setCourtLoading((prev) => ({ ...prev, districts: false }));
      return;
    }

    const debounce = setTimeout(async () => {
      setCourtLoading((prev) => ({ ...prev, districts: true }));
      const results = await listCourts({ district: search, ordering: 'district', limit: 100 });
      if (!isMounted) return;
      setDistrictResults(Array.isArray(results) ? results : []);
      setCourtLoading((prev) => ({ ...prev, districts: false }));
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(debounce);
      setCourtLoading((prev) => ({ ...prev, districts: false }));
    };
  }, [formData.judicial_district, courtSearchUi.showDistrictSuggestions, listCourts]);

  // Busca varas da comarca selecionada com debounce
  useEffect(() => {
    let isMounted = true;
    const district = normalizeSearchText(formData.judicial_district);
    const unit = normalizeSearchText(formData.court);

    if (!courtSearchUi.showCourtSuggestions || !district) {
      setCourtResults([]);
      setCourtLoading((prev) => ({ ...prev, courts: false }));
      return;
    }

    const debounce = setTimeout(async () => {
      setCourtLoading((prev) => ({ ...prev, courts: true }));
      const results = await listCourts({
        district,
        ...(unit ? { unit } : {}),
        ordering: 'unit',
        limit: 100,
      });
      if (!isMounted) return;
      setCourtResults(Array.isArray(results) ? results : []);
      setCourtLoading((prev) => ({ ...prev, courts: false }));
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(debounce);
      setCourtLoading((prev) => ({ ...prev, courts: false }));
    };
  }, [formData.judicial_district, formData.court, courtSearchUi.showCourtSuggestions, listCourts]);

  const unitParam = useMemo(() => effectiveUnitId ? { unit: effectiveUnitId } : {}, [effectiveUnitId]);

  const loadCounts = useCallback(async () => {
    const t = todayStr();
    const [total, hoje, agendadas, realizadas] = await Promise.all([
      listHearings({ limit: 1, ...unitParam }),
      listHearings({ limit: 1, start_date_from: t, start_date_to: t, ...unitParam }),
      listHearings({ limit: 1, completion_status: 'scheduled', ...unitParam }),
      listHearings({ limit: 1, completion_status: 'true', ...unitParam }),
    ]);
    setCounts({
      total: total.count ?? 0,
      hoje: hoje.count ?? 0,
      agendadas: agendadas.count ?? 0,
      realizadas: realizadas.count ?? 0,
    });
  }, [listHearings, unitParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTodayHearings = useCallback(async () => {
    const t = todayStr();
    const data = await listHearings({ limit: 20, start_date_from: t, start_date_to: t, ...unitParam });
    setTodayHearings(data?.results || []);
  }, [listHearings, unitParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHearings = useCallback(async (page = 0) => {
    setIsLoading(true);
    const offset = page * pageSize;
    const params = { limit: pageSize, offset, ...unitParam };
    Object.entries(filters).forEach(([k, v]) => { if (v !== '') params[k] = v; });
    const data = await listHearings(params);
    const results = data?.results || [];
    setHearings(results);
    setTotalItems(data?.count ?? 0);
    setIsLoading(false);

    const ids = [...new Set(results.map((h) => h.scheduling).filter(Boolean))];
    if (ids.length > 0) {
      const entries = await Promise.all(ids.map(async (id) => {
        const s = await getSchedulingById(id);
        return [id, { virtual: s?.virtual ?? null, observation: s?.observation || '' }];
      }));
      setVirtualMap(Object.fromEntries(entries));
    }
  }, [listHearings, filters, unitParam, getSchedulingById, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCounts();
    loadTodayHearings();
  }, [loadCounts, loadTodayHearings]);

  useEffect(() => {
    loadHearings(currentPage);
  }, [currentPage, filters, loadHearings]);

  const handlePageClick = ({ selected }) => setCurrentPage(selected);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(0);
  };

  const handleSelectInternal = async (internal) => {
    const unitLabel = internal.unit_name || internal.unit_acronym || '';
    setFormData((prev) => ({ ...prev, internal: internal.id, internalName: internal.name, internalUnit: unitLabel, process: '' }));
    setFormErrors((prev) => ({ ...prev, internal: undefined }));
    setInternalProcesses([]);
    setIsLoadingProcesses(true);
    try {
      const data = await getProcessInternalById(internal.id);
      const list = Array.isArray(data) ? data : (data?.results || []);
      setInternalProcesses(list.filter((p) => p.is_active !== false));
    } catch (_) {
      setInternalProcesses([]);
    } finally {
      setIsLoadingProcesses(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSelectDistrict = useCallback((district) => {
    setFormData((prev) => ({ ...prev, judicial_district: district, court: '', court_id: '' }));
    setFormErrors((prev) => ({ ...prev, judicial_district: undefined, court: undefined }));
    setCourtSearchUi((prev) => ({ ...prev, showDistrictSuggestions: false }));
    setCourtResults([]);
  }, []);

  const handleSelectCourt = useCallback((courtItem) => {
    setFormData((prev) => ({
      ...prev,
      court: courtItem?.unit || '',
      court_id: courtItem?.id || '',
    }));
    setFormErrors((prev) => ({ ...prev, court: undefined }));
    setCourtSearchUi((prev) => ({ ...prev, showCourtSuggestions: false }));
  }, []);

  const handleOpenModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setInternalProcesses([]);
    setDistrictResults([]);
    setCourtResults([]);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = useCallback(async (hearing) => {
    const formatForInput = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setIsEditing(true);
    setEditingId(hearing.id);
    setFormData({
      internal: hearing.internal_id || '',
      internalName: hearing.internal_name || '',
      internalUnit: hearing.unit_name || hearing.unit_acronym || '',
      start_date: formatForInput(hearing.start),
      end_date: formatForInput(hearing.end),
      virtual: hearing.virtual || false,
      judicial_district: hearing.district || '',
      court: hearing.court_name || '',
      court_id: hearing.court || '',
      process: hearing.process != null ? String(hearing.process) : '',
      whatsapp_link: hearing.whatsapp_link || '',
      observations: hearing.observations || '',
    });
    setFormErrors({});
    setDistrictResults([]);
    setCourtResults([]);
    setIsModalOpen(true);

    if (hearing.internal_id) {
      setIsLoadingProcesses(true);
      try {
        const data = await getProcessInternalById(hearing.internal_id);
        const list = Array.isArray(data) ? data : (data?.results || []);
        setInternalProcesses(list.filter((p) => p.is_active !== false));
      } catch (_) {
        setInternalProcesses([]);
      } finally {
        setIsLoadingProcesses(false);
      }
    } else {
      setInternalProcesses([]);
    }
  }, [getProcessInternalById]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.internal) errors.internal = 'Selecione um interno.';
    if (!formData.judicial_district) errors.judicial_district = 'Informe a comarca.';
    if (!formData.court_id) errors.court = 'Selecione uma vara da lista.';
    if (!formData.start_date) errors.start_date = 'Informe a data e hora de início.';
    if (!isEditing && formData.start_date && new Date(formData.start_date) <= new Date()) {
      errors.start_date = 'Não é permitido agendar audiências no passado.';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    await runWithSubmitLock(async () => {
      const payload = {
        internal: formData.internal,
        type_schedule: 19,
        start_date: new Date(formData.start_date).toISOString(),
        court: Number(formData.court_id),
      };
      if (formData.end_date) payload.end_date = new Date(formData.end_date).toISOString();
      if (formData.virtual) payload.virtual = true;
      if (formData.process) payload.process = Number(formData.process);
      if (formData.whatsapp_link) payload.whatsapp_link = formData.whatsapp_link;
      if (formData.observations) payload.observations = formData.observations;

      try {
        if (isEditing) {
          await updateHearing(editingId, payload);
        } else {
          await createHearing(payload);
        }
        setIsModalOpen(false);
        loadCounts();
        loadTodayHearings();
        setCurrentPage(0);
        loadHearings(0);
      } catch (error) {
        const data = error?.response?.data;
        if (data && typeof data === 'object') {
          const apiErrors = {};
          Object.entries(data).forEach(([key, val]) => {
            const msg = Array.isArray(val) ? val[0] : String(val);
            apiErrors[key] = msg;
          });
          setFormErrors(apiErrors);
        }
      }
    });
  };

  const handleOpenViewModal = useCallback(async (h) => {
    setViewModal({ open: true, hearing: h, processNumber: null });
    if (h.process && h.internal_id) {
      try {
        const data = await getProcessInternalById(h.internal_id);
        const list = Array.isArray(data) ? data : (data?.results || []);
        const found = list.find((p) => p.id === h.process);
        if (found?.number) {
          setViewModal((prev) => ({ ...prev, processNumber: found.number }));
        }
      } catch (_) {}
    }
  }, [getProcessInternalById]);

  const refreshHearing = useCallback(async () => {
    loadCounts();
    loadTodayHearings();
    loadHearings(currentPage);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartProgress = useCallback(async (id) => {
    try {
      await startHearingProgress(id);
      refreshHearing();
    } catch (_) {}
  }, [startHearingProgress, refreshHearing]);

  const handleOpenStatusModal = useCallback((hearing) => {
    setStatusModal({ open: true, hearing });
    setStatusForm({ completion_status: '', observations: '' });
  }, []);

  const handleSubmitStatus = useCallback(async () => {
    if (statusForm.completion_status === '') return;
    try {
      await updateHearingStatus(statusModal.hearing.id, {
        completion_status: statusForm.completion_status === 'true',
        observations: statusForm.observations,
      });
      setStatusModal({ open: false, hearing: null });
      refreshHearing();
    } catch (_) {}
  }, [statusModal, statusForm, updateHearingStatus, refreshHearing]);

  const handleUploadProof = useCallback(async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    try {
      await uploadHearingProof(id, file);
      refreshHearing();
    } catch (_) {}
    setUploadingId(null);
  }, [uploadHearingProof, refreshHearing]);

  const pageCount = Math.ceil(totalItems / pageSize) || 0;

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="container">
        <Titulo
          children={<span><FontAwesomeIcon icon={faGavel} /> Audiências</span>}
          children2={
            <VisibleAccess compareField="groups" compareValues={['DUAP']}>
              <button className="hearing-btn-add" onClick={handleOpenModal}>
                <FontAwesomeIcon icon={faPlus} /> Nova Audiência
              </button>
            </VisibleAccess>
          }
        />

        {/* Cards de contagem */}
        <div className="hearing-counts">
          <div className="hearing-count-card">
            <span className="hearing-count-label">Total</span>
            <span className="hearing-count-value">{counts.total}</span>
          </div>
          <div className="hearing-count-card hearing-count-card--hoje">
            <span className="hearing-count-label">Hoje</span>
            <span className="hearing-count-value">{counts.hoje}</span>
          </div>
          <div className="hearing-count-card hearing-count-card--agendadas">
            <span className="hearing-count-label">Agendadas</span>
            <span className="hearing-count-value">{counts.agendadas}</span>
          </div>
          <div className="hearing-count-card hearing-count-card--realizadas">
            <span className="hearing-count-label">Realizadas</span>
            <span className="hearing-count-value">{counts.realizadas}</span>
          </div>
        </div>

        {/* Audiências do dia */}
        <div className="hearing-today-section">
          <p className="hearing-today-title">Audiências do Dia</p>
          {todayHearings.length === 0 ? (
            <p className="hearing-empty">Nenhuma audiência agendada para hoje.</p>
          ) : (
            <div className="hearing-today-list">
              {todayHearings.map((h) => {
                const badge = statusBadge(h);
                return (
                  <div key={h.id} className="hearing-today-item">
                    <span className="hearing-today-time">
                      {h.start ? new Date(h.start).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </span>
                    <span className="hearing-today-name">{h.internal_name}</span>
                    {h.unit_acronym && <span className="hearing-today-unit">{h.unit_acronym}</span>}
                    <span className="hearing-today-court">{h.court_name}</span>
                    <span className={`hearing-badge ${badge.cls}`}>{badge.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Filtros */}
        <div className="hearing-filters">
          <div className="hearing-filter-group">
            <label>Interno</label>
            <input
              name="internal_name"
              value={filters.internal_name}
              onChange={handleFilterChange}
              placeholder="Nome do interno"
            />
          </div>
          <div className="hearing-filter-group">
            <label>Vara / Comarca</label>
            <input
              name="court_name"
              value={filters.court_name}
              onChange={handleFilterChange}
              placeholder="Nome da vara"
            />
          </div>
          <div className="hearing-filter-group">
            <label>Status</label>
            <select name="completion_status" value={filters.completion_status} onChange={handleFilterChange}>
              <option value="">Todos</option>
              <option value="scheduled">Agendadas</option>
              <option value="pending">Pendentes</option>
              <option value="true">Realizadas</option>
              <option value="false">Não realizadas</option>
            </select>
          </div>
          {isPrivilegedUser && (
            <div className="hearing-filter-group">
              <label>Unidade</label>
              <select name="unit" value={filters.unit} onChange={handleFilterChange}>
                <option value="">Todas</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>{u.acronym} — {u.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="hearing-filter-group">
            <label>De</label>
            <input type="date" name="start_date_from" value={filters.start_date_from} onChange={handleFilterChange} />
          </div>
          <div className="hearing-filter-group">
            <label>Até</label>
            <input type="date" name="start_date_to" value={filters.start_date_to} onChange={handleFilterChange} />
          </div>
        </div>

        {/* Lista de audiências */}
        <div className="hearing-list" style={{ marginTop: 16 }}>
          {isLoading ? (
            <p className="hearing-empty">Carregando...</p>
          ) : hearings.length === 0 ? (
            <p className="hearing-empty">Nenhuma audiência encontrada.</p>
          ) : (
            hearings.map((h) => {
              const badge = statusBadge(h);
              return (
                <div key={h.id} className="hearing-card">
                  <div className="hearing-card-header">
                    <span className="hearing-card-name">{h.internal_name}</span>
                    <span className="hearing-card-unit">{h.unit_acronym}</span>
                    <span className={`hearing-badge ${badge.cls}`}>{badge.label}</span>
                    <button
                      className="hearing-btn-view"
                      title="Visualizar audiência"
                      onClick={() => handleOpenViewModal(h)}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    {h.completion_status === null && (
                      <VisibleAccess compareField="groups" compareValues={['DUAP']}>
                        <button
                          className="hearing-btn-edit"
                          title="Editar audiência"
                          onClick={() => handleOpenEditModal(h)}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                      </VisibleAccess>
                    )}
                  </div>
                  <div className="hearing-card-grid">
                    {h.scheduling != null && virtualMap[h.scheduling]?.virtual !== undefined && virtualMap[h.scheduling]?.virtual !== null && (
                      <div className="hearing-card-field">
                        <span className="hearing-card-field-label">Modalidade</span>
                        <span className={`hearing-modality-badge ${virtualMap[h.scheduling]?.virtual ? 'hearing-modality-badge--virtual' : 'hearing-modality-badge--presencial'}`}>
                          {virtualMap[h.scheduling]?.virtual ? 'Videoconferência' : 'Presencial'}
                        </span>
                      </div>
                    )}
                    <div className="hearing-card-field">
                      <span className="hearing-card-field-label">Início</span>
                      <span className="hearing-card-field-value">{formatHearingDate(h.start)}</span>
                    </div>
                    <div className="hearing-card-field">
                      <span className="hearing-card-field-label">Vara</span>
                      <span className="hearing-card-field-value">{h.court_name || '—'}</span>
                    </div>
                    {h.observations && (
                      <div className="hearing-card-field" style={{ gridColumn: '1 / -1' }}>
                        <span className="hearing-card-field-label">Observações</span>
                        <span className="hearing-card-field-value">{h.observations}</span>
                      </div>
                    )}
                    {(() => {
                      const link = resolveHearingLink(h.whatsapp_link);
                      if (!link) return null;
                      return (
                        <div className="hearing-card-field">
                          <span className="hearing-card-field-label">Link</span>
                          <span className="hearing-card-field-value">
                            <a href={link.href} target="_blank" rel="noreferrer">{link.label}</a>
                          </span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Ações pós-agendamento */}
                  <div className="hearing-card-actions">
                    {!h.in_progress && h.completion_status === null && (
                      <button
                        className="hearing-action-btn hearing-action-btn--start"
                        onClick={() => handleStartProgress(h.id)}
                      >
                        ▶ Iniciar
                      </button>
                    )}
                    {h.in_progress && (
                      <button
                        className="hearing-action-btn hearing-action-btn--conclude"
                        onClick={() => handleOpenStatusModal(h)}
                      >
                        ✓ Concluir
                      </button>
                    )}
                    {h.completion_status !== null && !hasBinaryField(h, 'proof_file') && (
                      <>
                        <label className="hearing-action-btn hearing-action-btn--upload">
                          {uploadingId === h.id ? 'Enviando...' : '↑ Documento'}
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            style={{ display: 'none' }}
                            disabled={uploadingId === h.id}
                            onChange={(e) => handleUploadProof(h.id, e.target.files[0])}
                          />
                        </label>
                      </>
                    )}
                    {hasBinaryField(h, 'proof_file') && (
                      <BinaryFileLink
                        resource={BINARY_RESOURCES.hearing}
                        id={h.id}
                        field="proof_file"
                        className="hearing-action-btn hearing-action-btn--download"
                      >
                        ↓ Documento
                      </BinaryFileLink>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Paginação */}
        <div className="hearing-pagination-row">
          <div className="hearing-page-size">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(0);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          {pageCount > 1 && (
            <ReactPaginate
              previousLabel={'<'}
              nextLabel={'>'}
              breakLabel={'...'}
              pageCount={pageCount}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              onPageChange={handlePageClick}
              containerClassName={'pagination'}
              activeClassName={'activeNumber'}
              previousClassName={'prev'}
              nextClassName={'next'}
              pageClassName={'page'}
              breakClassName={'break-me'}
              forcePage={currentPage}
            />
          )}
        </div>
      </div>

      {/* Modal de visualização */}
      <Modal isOpen={viewModal.open} closeModal={() => setViewModal({ open: false, hearing: null })} title="Detalhes da Audiência">
        {viewModal.hearing && (() => {
          const h = viewModal.hearing;
          const badge = statusBadge(h);
          const link = resolveHearingLink(h.whatsapp_link);
          const schedulingData = h.scheduling != null ? virtualMap[h.scheduling] : undefined;
          const isVirtual = schedulingData?.virtual;
          return (
            <div className="hearing-view-modal">
              <div className="hearing-view-header">
                <div>
                  <p className="hearing-view-name">{h.internal_name}</p>
                  <p className="hearing-view-unit">{h.unit_name || h.unit_acronym}</p>
                </div>
                <span className={`hearing-badge ${badge.cls}`}>{badge.label}</span>
              </div>
              <div className="hearing-view-grid">
                {isVirtual !== undefined && isVirtual !== null && (
                  <div className="hearing-view-field">
                    <span className="hearing-card-field-label">Modalidade</span>
                    <span className={`hearing-modality-badge ${isVirtual ? 'hearing-modality-badge--virtual' : 'hearing-modality-badge--presencial'}`}>
                      {isVirtual ? 'Videoconferência' : 'Presencial'}
                    </span>
                  </div>
                )}
                <div className="hearing-view-field">
                  <span className="hearing-card-field-label">Início</span>
                  <span className="hearing-card-field-value">{formatHearingDate(h.start)}</span>
                </div>
                {h.end && (
                  <div className="hearing-view-field">
                    <span className="hearing-card-field-label">Fim</span>
                    <span className="hearing-card-field-value">{formatHearingDate(h.end)}</span>
                  </div>
                )}
                <div className="hearing-view-field">
                  <span className="hearing-card-field-label">Vara</span>
                  <span className="hearing-card-field-value">{h.court_name || '—'}</span>
                </div>
                <div className="hearing-view-field">
                  <span className="hearing-card-field-label">Comarca</span>
                  <span className="hearing-card-field-value">{h.district || '—'}</span>
                </div>
                {h.process && (
                  <div className="hearing-view-field">
                    <span className="hearing-card-field-label">Processo</span>
                    <span className="hearing-card-field-value">
                      {viewModal.processNumber || `#${h.process}`}
                    </span>
                  </div>
                )}
                {link && (
                  <div className="hearing-view-field">
                    <span className="hearing-card-field-label">Link</span>
                    <span className="hearing-card-field-value">
                      <a href={link.href} target="_blank" rel="noreferrer">{link.label}</a>
                    </span>
                  </div>
                )}
                {h.justified && (
                  <div className="hearing-view-field">
                    <span className="hearing-card-field-label">Situação</span>
                    <span className="hearing-card-field-value">Justificada</span>
                  </div>
                )}
                {h.justified && schedulingData?.observation && (
                  <div className="hearing-view-field hearing-view-field--full">
                    <span className="hearing-card-field-label">Motivo da justificativa</span>
                    <span className="hearing-card-field-value">{schedulingData.observation}</span>
                  </div>
                )}
                {hasBinaryField(h, 'proof_file') && (
                  <div className="hearing-view-field">
                    <span className="hearing-card-field-label">Documento</span>
                    <span className="hearing-card-field-value">
                      <BinaryFileLink resource={BINARY_RESOURCES.hearing} id={h.id} field="proof_file">Baixar documento</BinaryFileLink>
                    </span>
                  </div>
                )}
                {h.observations && (
                  <div className="hearing-view-field hearing-view-field--full">
                    <span className="hearing-card-field-label">Observações</span>
                    <span className="hearing-card-field-value">{h.observations}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Modal de conclusão */}
      <Modal isOpen={statusModal.open} closeModal={() => setStatusModal({ open: false, hearing: null })} title="Concluir Audiência">
        <div className="hearing-form">
          <p className="hearing-status-modal-name">{statusModal.hearing?.internal_name}</p>
          <div className="hearing-form-group">
            <label>Resultado *</label>
            <div className="hearing-status-options">
              <label className={`hearing-status-option ${statusForm.completion_status === 'true' ? 'hearing-status-option--selected' : ''}`}>
                <input
                  type="radio"
                  name="completion_status"
                  value="true"
                  checked={statusForm.completion_status === 'true'}
                  onChange={(e) => setStatusForm((prev) => ({ ...prev, completion_status: e.target.value }))}
                />
                Realizada
              </label>
              <label className={`hearing-status-option ${statusForm.completion_status === 'false' ? 'hearing-status-option--selected hearing-status-option--notdone' : ''}`}>
                <input
                  type="radio"
                  name="completion_status"
                  value="false"
                  checked={statusForm.completion_status === 'false'}
                  onChange={(e) => setStatusForm((prev) => ({ ...prev, completion_status: e.target.value }))}
                />
                Não realizada
              </label>
            </div>
          </div>
          <div className="hearing-form-group">
            <label>Observações</label>
            <textarea
              value={statusForm.observations}
              onChange={(e) => setStatusForm((prev) => ({ ...prev, observations: e.target.value }))}
              rows={3}
              placeholder="Motivo, ocorrências..."
            />
          </div>
          <div className="hearing-form-actions">
            <button type="button" className="hearing-btn-cancel" onClick={() => setStatusModal({ open: false, hearing: null })}>
              Cancelar
            </button>
            <button
              type="button"
              className="hearing-btn-submit"
              disabled={statusForm.completion_status === ''}
              onClick={handleSubmitStatus}
            >
              Salvar
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de criação / edição */}
      <Modal isOpen={isModalOpen} closeModal={() => setIsModalOpen(false)} title={isEditing ? 'Editar Audiência' : 'Nova Audiência'}>
        <form onSubmit={handleSubmit} className="hearing-form">

          <div className="hearing-form-group">
            <label>Interno *</label>
            {isEditing ? (
              <div className="hearing-selected-internal">
                <span>{formData.internalName}</span>
              </div>
            ) : formData.internalName ? (
              <div className="hearing-selected-internal">
                <span>{formData.internalName}</span>
                <button
                  type="button"
                  className="hearing-btn-clear"
                  onClick={() => { setFormData((prev) => ({ ...prev, internal: '', internalName: '', internalUnit: '', process: '' })); setInternalProcesses([]); }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <SearchInternal onSelect={handleSelectInternal} />
            )}
            {formData.internalUnit && (
              <span className="hearing-internal-unit">Unidade: {formData.internalUnit}</span>
            )}
            {formErrors.internal && <span className="hearing-field-error">{formErrors.internal}</span>}
          </div>

          {/* Comarca */}
          <div className="hearing-form-group">
            <label>Comarca *</label>
            <div className="hearing-form-field">
              <input
                type="text"
                name="judicial_district"
                value={formData.judicial_district}
                placeholder="Digite para buscar a comarca"
                autoComplete="off"
                className={formErrors.judicial_district ? 'input-error' : ''}
                onFocus={() => setCourtSearchUi((prev) => ({ ...prev, showDistrictSuggestions: true }))}
                onBlur={() => setTimeout(() => setCourtSearchUi((prev) => ({ ...prev, showDistrictSuggestions: false })), 150)}
                onChange={(e) => {
                  handleSelectDistrict(e.target.value);
                  setCourtSearchUi((prev) => ({ ...prev, showDistrictSuggestions: true }));
                }}
              />
              {courtLoading.districts && (
                <span className="hearing-search-loading">Buscando comarcas...</span>
              )}
              {courtSearchUi.showDistrictSuggestions && filteredDistrictSuggestions.length > 0 && (
                <ul className="hearing-suggestions">
                  {filteredDistrictSuggestions.map((comarca) => (
                    <li key={comarca} onMouseDown={() => handleSelectDistrict(comarca)}>
                      {comarca}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {formErrors.judicial_district && <span className="hearing-field-error">{formErrors.judicial_district}</span>}
          </div>

          {/* Vara */}
          <div className="hearing-form-group">
            <label>Vara *</label>
            <div className="hearing-form-field">
              <input
                type="text"
                name="court"
                value={formData.court}
                placeholder={formData.judicial_district ? 'Digite para buscar a vara' : 'Selecione a comarca primeiro'}
                autoComplete="off"
                disabled={!formData.judicial_district}
                className={formErrors.court ? 'input-error' : ''}
                onFocus={() => setCourtSearchUi((prev) => ({ ...prev, showCourtSuggestions: true }))}
                onBlur={() => setTimeout(() => setCourtSearchUi((prev) => ({ ...prev, showCourtSuggestions: false })), 150)}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, court: e.target.value, court_id: '' }));
                  setFormErrors((prev) => ({ ...prev, court: undefined }));
                  setCourtSearchUi((prev) => ({ ...prev, showCourtSuggestions: true }));
                }}
              />
              {courtLoading.courts && (
                <span className="hearing-search-loading">Buscando varas...</span>
              )}
              {courtSearchUi.showCourtSuggestions && filteredCourtSuggestions.length > 0 && (
                <ul className="hearing-suggestions">
                  {filteredCourtSuggestions.map((vara) => (
                    <li
                      key={vara}
                      onMouseDown={() => {
                        const found = courtResults.find((c) => c?.unit === vara);
                        handleSelectCourt(found || { unit: vara, id: '' });
                      }}
                    >
                      {vara}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {formErrors.court && <span className="hearing-field-error">{formErrors.court}</span>}
          </div>

          <div className="hearing-form-row">
            <div className="hearing-form-group">
              <label>Data / hora início *</label>
              <input
                type="datetime-local"
                name="start_date"
                value={formData.start_date}
                onChange={handleFormChange}
                className={formErrors.start_date ? 'input-error' : ''}
              />
              {formErrors.start_date && <span className="hearing-field-error">{formErrors.start_date}</span>}
            </div>
            <div className="hearing-form-group">
              <label>Data / hora fim</label>
              <input
                type="datetime-local"
                name="end_date"
                value={formData.end_date}
                onChange={handleFormChange}
                className={formErrors.end_date ? 'input-error' : ''}
              />
              {formErrors.end_date && <span className="hearing-field-error">{formErrors.end_date}</span>}
            </div>
          </div>

          <div className="hearing-form-row">
            <div className="hearing-form-group">
              <label>Processo</label>
              <select
                name="process"
                value={formData.process}
                onChange={handleFormChange}
                disabled={!formData.internal || isLoadingProcesses}
              >
                <option value="">
                  {!formData.internal
                    ? 'Selecione o interno primeiro'
                    : isLoadingProcesses
                      ? 'Carregando processos...'
                      : internalProcesses.length === 0
                        ? 'Nenhum processo encontrado'
                        : 'Selecione (opcional)'}
                </option>
                {internalProcesses.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.number || `Processo #${p.id}`}
                    {p.judicial_district ? ` — ${p.judicial_district}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="hearing-form-group">
            <label>Link da audiência</label>
            <input
              type="url"
              name="whatsapp_link"
              value={formData.whatsapp_link}
              onChange={handleFormChange}
              placeholder="https://meet.google.com/... ou https://wa.me/..."
            />
          </div>

          {!isEditing && (
            <div className="hearing-form-group hearing-form-check">
              <label>
                <input
                  type="checkbox"
                  name="virtual"
                  checked={formData.virtual}
                  onChange={handleFormChange}
                />
                &nbsp;Audiência virtual
              </label>
            </div>
          )}

          <div className="hearing-form-group">
            <label>Observações</label>
            <textarea
              name="observations"
              value={formData.observations}
              onChange={handleFormChange}
              rows={3}
              placeholder="Observações sobre a audiência..."
            />
          </div>

          <div className="hearing-form-actions">
            <button type="button" className="hearing-btn-cancel" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </button>
            <button type="submit" className="hearing-btn-submit" disabled={isSubmitting}>
              {isSubmitting ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
